import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { Loader2 } from "lucide-react"

import { cn } from "../../lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[10px] text-sm font-semibold ring-offset-background transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4C6FFF]/30 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:scale-[0.98]",
  {
    variants: {
      variant: {
        default:
          "bg-brand text-white shadow-[0_1px_3px_rgba(31,42,68,0.2)] hover:bg-brand-hover hover:shadow-[0_3px_10px_rgba(31,42,68,0.22)]",
        /* primary = dark navy CTA — use for the most important action on a page */
        primary:
          "bg-brand text-white shadow-[0_1px_3px_rgba(31,42,68,0.2)] hover:bg-brand-hover hover:shadow-[0_3px_10px_rgba(31,42,68,0.22)]",
        accent:
          "bg-accent text-white shadow-[0_1px_2px_rgba(76,111,255,0.2)] hover:bg-accent-hover hover:shadow-[0_4px_12px_-2px_rgba(76,111,255,0.3)] focus-visible:ring-[#4C6FFF]/35",
        destructive:
          "bg-danger text-white shadow-sm hover:bg-[#B91C1C]",
        outline:
          "border border-border bg-white text-foreground shadow-[0_1px_2px_rgba(0,0,0,0.04)] hover:bg-background hover:border-[color:var(--color-border-strong)]",
        secondary:
          "bg-panel text-foreground hover:bg-[#E2E8F0]",
        ghost:
          "text-muted-foreground hover:bg-panel hover:text-foreground",
        link:
          "text-accent underline-offset-4 hover:underline p-0 h-auto font-medium",
      },
      size: {
        default: "h-10 px-4",
        md: "h-10 px-4",
        sm: "h-8 px-3 text-xs",
        lg: "h-11 px-6",
        xl: "h-12 px-8 text-[15px]",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, disabled, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    const isDisabled = disabled || loading
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={isDisabled}
        aria-disabled={isDisabled}
        {...props}
      >
        {loading && <Loader2 className="h-4 w-4 animate-spin" />}
        {children}
      </Comp>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
